import { Component, Input, forwardRef } from '@angular/core';
import { AbstractControl, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { CacheMap } from '@app/classes';
import { Country } from '@app/models';
import { DictionaryService } from '@app/services';
import { DEFAULT_COUNTRY_CODE } from '@app/shared';
import { Utils } from 'app/core/utils';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-country-selector',
  templateUrl: './country-selector.component.html',
  styleUrls: ['./country-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CountrySelectorComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => CountrySelectorComponent),
    },
  ],
})
export class CountrySelectorComponent {
  @Input() countries: CacheMap<Country>;
  @Input() isRequired: boolean = false;
  country: FormControl = new FormControl('');
  isDisabled: boolean = false;
  subscriptions: Subscription[] = [];

  constructor(private dictionary: DictionaryService) {}

  private _onChange = (_: Country | undefined) => undefined;
  registerOnChange(fn: (_: Country | undefined) => undefined): void {
    this._onChange = fn;
  }

  private _onTouched = () => undefined;
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) this.country.disable();
    else this.country.enable();
  }

  validate(control: AbstractControl<any, any>): ValidationErrors | null {
    const isEmpty = !control.value;
    if (!isEmpty && !control.touched) {
      control.markAsTouched();
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    if (!control.touched) return null;
    this.isRequired = control.hasValidator(Validators.required);
    if (this.isRequired && isEmpty) {
      return { required: true };
    }
    return null;
  }

  ngOnDestroy(): void {
    this.subscriptions = Utils.unsubscribe(this.subscriptions);
  }

  ngOnInit(): void {
    const countrySub = this.country.valueChanges.subscribe((value: Country) => this._onChange(value));
    this.subscriptions.push(countrySub);
  }

  async patchDefaultCountryValue(): Promise<Country | undefined> {
    this.countries ??= await this.dictionary.getCountries();
    return this.countries.findByProp('countryCode', DEFAULT_COUNTRY_CODE);
  }

  writeValue(country: Country): void {
    if (country.countryId && this.countries) {
      const selectedCountry = this.countries.with(country.countryId).get(country.countryId);
      this.country.patchValue(selectedCountry);
      this._onTouched();
    } else {
      this.patchDefaultCountryValue().then((country) => {
        this.country.patchValue(country);
        this._onChange(country);
      });
    }
  }

  onTouched() {
    this._onTouched();
  }
}
